import { Flex, LegacyText, type themedColor } from '@lemonade-hq/cdk';
import parse from 'html-react-parser';
import type React from 'react';
import type { HTMLAttributes } from 'react';
import styled from 'styled-components';
import { adjustContentVisibility } from '../shared/Content';
import { ResponsiveSubtitle, Subtitle, Title } from './Elements';
import {
  CoverageContainer,
  CoverageItem,
  CoverageItemDescription,
  CoverageItemImage,
  CoverageItemInfo,
  CoverageItemTitle,
  Section,
} from './StyledElements';

const StyledSection = styled(Section)`
  width: 100vw;
  text-align: center;
  ${adjustContentVisibility}
`;

export const CoverageItemTitleText = styled(LegacyText).attrs<unknown>({
  fontSize: ['2.0rem', '2.2rem', '2.6rem'],
  fontWeight: 'bold',
  lineHeight: ['2.4rem', '3rem', '3.4rem'],
  textAlign: 'center',
  as: 'h2',
  type: 'main',
})``;

export const CoverageItemDescriptionText = styled(LegacyText).attrs<unknown>({
  fontSize: ['1.4rem', '1.6rem', '1.6rem'],
  color: '#797979',
  lineHeight: ['1.9rem', '2.1rem', '2.1rem'],
  textAlign: 'center',
  as: 'p',
  type: 'main',
  width: 214,
})``;

type ThemedColor = Parameters<typeof themedColor>[0];

export interface Coverage {
  id?: string;
  title: string | string[];
  description?: string | string[];
  alt: string;
  background: string;
  backgroundDesktop?: string;
  altColorBackground?: string;
  altColorBackgroundDesktop?: string;
  preElement?: React.ReactElement;
  pill?: {
    text: string;
    backgroundColor?: ThemedColor;
  };
}

interface Props extends HTMLAttributes<HTMLDivElement> {
  title: string;
  subtitle?: string | string[];
  coverages: Coverage[];
  itemWidth?: number | [number, number] | [number, number, number];
  itemMargin?: number | [number, number] | [number, number, number];
  containerWidth?: number | [number, number] | [number, number, number];
  darkBackground?: boolean;
}

export const CoverageSection: React.FC<Props> = ({
  title,
  subtitle,
  coverages,
  itemMargin = 30,
  itemWidth = [210, 270],
  containerWidth,
  ...props
}) => {
  const subtitles = typeof subtitle === 'string' ? [subtitle] : subtitle;
  return (
    <StyledSection mb={0} pt={[45, 100]} width={1} contain="paint" {...props}>
      <Title width={[315, 1]} mb={13} tabIndex={0}>
        {parse(title)}
      </Title>
      <ResponsiveSubtitle width={[330, 1]}>
        {[
          // eslint-disable-next-line react/jsx-key
          <span>{parse(subtitles?.join(' ') ?? '')}</span>,
          // eslint-disable-next-line react/jsx-key
          <Flex flexDirection="column">
            {subtitles?.map(subtitle => (
              <Subtitle key={subtitle} width={[330, 1]} tabIndex={0}>
                {parse(subtitle)}
              </Subtitle>
            ))}
          </Flex>,
        ]}
      </ResponsiveSubtitle>

      <CoverageContainer
        mt={[45, 80, 80]}
        px={[40, 0]}
        pb={[50, 100, 100, 100]}
        width={[1, 980, 1]}
        justifyContent={['flex-start', 'center']}
        flexWrap={['nowrap', 'wrap']}
        containerWidth={containerWidth}
        role="list"
      >
        {coverages.map(({ background, backgroundDesktop, title, description, alt, preElement: sideNote }) => {
          const titles = typeof title === 'string' ? [title] : title;
          const descriptions = typeof description === 'string' ? [description] : description;

          return (
            <CoverageItem
              key={titles.join()}
              alignItems="center"
              position="relative"
              flexBasis={itemWidth}
              minWidth={itemWidth}
              mx={itemMargin}
              role="listitem"
            >
              {sideNote}
              <CoverageItemImage
                alt={alt}
                src={backgroundDesktop || background}
                width={165}
                height={165}
                tabIndex={-1}
              />
              <CoverageItemInfo tabIndex={0}>
                <CoverageItemTitle flexDirection="column">
                  {titles.map(title => (
                    <CoverageItemTitleText key={title}>{parse(title)}</CoverageItemTitleText>
                  ))}
                </CoverageItemTitle>
                {descriptions && (
                  <CoverageItemDescription flexDirection="column">
                    {descriptions.map(description => (
                      <CoverageItemDescriptionText key={description} style={{ whiteSpace: 'pre-line' }}>
                        {parse(description)}
                      </CoverageItemDescriptionText>
                    ))}
                  </CoverageItemDescription>
                )}
              </CoverageItemInfo>
            </CoverageItem>
          );
        })}
      </CoverageContainer>
    </StyledSection>
  );
};
